import { useState, useEffect } from 'react';
import type { SyncState } from '@/services/sync-state/types';
import { useSyncMachineService } from '@/contexts/sync-machine-context';

/**
 * Returns a snapshot of the current SyncState for every active vault
 * machine, keyed by vault ID.  Re-renders whenever any machine transitions.
 *
 * Useful for global UI that needs to reflect the "worst" or "most active"
 * sync status across all vaults (e.g. SyncProgressBar).
 */
export function useAllSyncStates(): Map<string, SyncState> {
  const service = useSyncMachineService();

  const [states, setStates] = useState<Map<string, SyncState>>(() => {
    const initial = new Map<string, SyncState>();
    for (const { vaultId, state } of service.getAllStates()) {
      initial.set(vaultId, state);
    }
    return initial;
  });

  useEffect(() => {
    // Snapshot current states (machines may have changed since render)
    const snapshot = new Map<string, SyncState>();
    for (const { vaultId, state } of service.getAllStates()) {
      snapshot.set(vaultId, state);
    }
    setStates(snapshot);

    const unsub = service.onAny((event) => {
      setStates((prev) => {
        const next = new Map(prev);
        next.set(event.vaultId, event.to);
        return next;
      });
    });

    return unsub;
  }, [service]);

  return states;
}
